import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Divider,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useReceptionById } from 'hooks';
import Loading from 'components/Loading';
import {
  checkDateUpperTimeInterval,
  createUTCDateFromDateString,
  formatDateForDisplay,
  toDisplayHour,
} from 'utils/date';
import { formatTonsFromKg } from 'utils/weight';
import { colors } from 'theme';
import { Routes } from 'routes';
import { AppContext } from 'context';
import { useLatestClosureDate } from 'utils/closureHooks';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteReception } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { goBack } from 'utils/history';
import { UserRole } from '../../types';

const useStyles = makeStyles({
  label: {
    fontWeight: 600,
    marginRight: 16,

    '&:after': {
      content: '" :"',
    },
  },
  receptionStatus: {
    borderRadius: '41px',
  },
  receptionAccepted: {
    backgroundColor: colors.greenSoft,
  },
  receptionRefused: {
    backgroundColor: colors.redSoft,
  },
  tablePaper: {
    maxWidth: '65%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  tablePaperRefused: {
    borderLeft: `6px solid ${colors.redPastel}`,
  },
  noBorder: {
    borderBottom: 'none',
  },
});

const ReceptionShow: React.FC = () => {
  const classes = useStyles();

  const history = useHistory();
  const { receptionId } = useParams<{ receptionId: string }>();

  const { t } = useTranslation();
  const { value: reception, loading } = useReceptionById(receptionId);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const receptionDate = reception ? createUTCDateFromDateString(reception.receptionDatetime) : new Date();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const canEdit = reception ? !readOnly && !checkDateUpperTimeInterval(receptionDate, closureMonth) : false;
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  return (
    <>
      <NoPaperPageContent
        title={t('receptions.show.title')}
        backAction={() => goBack(Routes.Receptions, history)}
        updateButtonLink={
          canEdit
            ? {
                pathname: generatePath(Routes.ReceptionEdit, { receptionId }),
                state: { referer: generatePath(Routes.ReceptionShow, { receptionId }) },
              }
            : undefined
        }
        deleteButtonAction={canEdit ? () => setConfirmDeleteModalOpen(true) : undefined}
      >
        {loading ? (
          <Loading />
        ) : (
          reception && (
            <>
              <DeleteConfirmationDialog
                title={t('receptionForm.deletion.confirm')}
                isOpen={confirmDeleteModalOpen}
                setOpen={open => setConfirmDeleteModalOpen(open)}
                confirmDeletion={async () => {
                  setConfirmDeleteModalOpen(false);

                  await deleteReception(receptionId);
                  notify({
                    message: t('receptionForm.deletion.success'),
                    severity: 'success',
                  });
                  goBack(Routes.Receptions, history);
                }}
              />
              <Box paddingTop="26px" paddingBottom="26px">
                <Chip
                  className={classNames(
                    classes.receptionStatus,
                    reception.refused ? classes.receptionRefused : classes.receptionAccepted,
                  )}
                  label={
                    reception.incrementRejectStock
                      ? t('receptions.show.miseEnRefus')
                      : reception.refused
                      ? t('receptions.show.refused')
                      : t('receptions.show.accepted')
                  }
                />
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="26px" maxWidth="70%">
                <Box flexGrow={1}>
                  <Typography>
                    <span className={classes.label}>{t('receptionForm.client')}</span>
                    {reception.client.shortName}
                  </Typography>
                </Box>
                <Box marginRight="26px">
                  <Typography>
                    <span className={classes.label}>{t('receptions.show.receptionDate')}</span>
                    {formatDateForDisplay(reception.receptionDatetime)}
                  </Typography>
                </Box>
                <Typography>
                  <span className={classes.label}>{t('receptions.show.receptionTime')}</span>
                  {toDisplayHour(reception.receptionDatetime)}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Typography>
                  <span className={classes.label}>{t('receptionForm.proofOfWeightIdentifier')}</span>
                  {reception.proofOfWeightIdentifier ?? '-'}
                </Typography>
              </Box>
              <Paper
                square
                className={classNames(classes.tablePaper, { [classes.tablePaperRefused]: reception.refused })}
              >
                <Box display="flex" marginTop="4em" paddingLeft="21px" paddingRight="21px">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('receptionForm.stream')}</TableCell>
                        <TableCell align="right">{t('receptionForm.grossWeight')}</TableCell>
                        <TableCell align="right">{t('receptionForm.tare')}</TableCell>
                        <TableCell align="right">{t('receptionForm.netWeight')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.noBorder}>{reception.stream.name}</TableCell>
                        <TableCell align="right" className={classes.noBorder}>
                          {formatTonsFromKg(reception.grossWeight)}
                        </TableCell>
                        <TableCell align="right" className={classes.noBorder}>
                          {formatTonsFromKg(reception.tare)}
                        </TableCell>
                        <TableCell align="right" className={classes.noBorder}>
                          {formatTonsFromKg(reception.netWeight)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </>
          )
        )}
      </NoPaperPageContent>
    </>
  );
};
export default ReceptionShow;
